import React, { useRef, useEffect } from 'react';
import { DemoChatProvider, useDemoChat } from './DemoChatProvider';
import StreamingMessage from '../StreamingMessage';
import InteractiveCards from '../InteractiveCards';
import CardErrorBoundary from '../CardErrorBoundary';

/**
 * DemoChatRenderer - Renders messages using real app components
 * 
 * This uses the actual StreamingMessage and InteractiveCards components
 * so the demo looks exactly like the real app.
 */
const DemoChatRenderer = ({ 
  className, 
  style,
  showHeader = false,
  headerContent,
  footerContent,
  onMessageClick,
  expandedCardId,
}) => {
  const demo = useDemoChat();
  const scrollRef = useRef(null);
  
  // Extract values with safe defaults (for hooks consistency)
  const visibleMessages = demo?.visibleMessages || [];
  const usecase = demo?.usecase;
  const isComplete = demo?.isComplete || false;

  // Auto-scroll to bottom when new messages appear
  useEffect(() => {
    if (scrollRef.current && visibleMessages.length > 0) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [visibleMessages.length]);
  
  if (!demo) {
    console.error('DemoChatRenderer must be used within DemoChatProvider');
    return null;
  }

  return (
    <div className={`flex flex-col h-full ${className || ''}`} style={style}>
      {/* Optional header */}
      {showHeader && headerContent && (
        <div className="flex-shrink-0">
          {headerContent}
        </div>
      )}
      
      {/* Messages area - uses real app styling */}
      <div 
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-3 sm:px-4 pb-4 chat-container"
        onClick={onMessageClick}
      >
        {visibleMessages.map((message, index) => {
          const cards = message.message_metadata?.interactive_cards || [];
          
          // Find if any card should be expanded
          const demoExpandIdx = expandedCardId 
            ? cards.findIndex(c => c.id === expandedCardId)
            : null;
          
          return (
            <div 
              key={message.id}
              className={`${message.role === 'user' ? 'card-base-user' : 'card-base-agent'} animate-fadeIn`}
            >
              {/* Real StreamingMessage component */}
              <StreamingMessage message={message} />
              
              {/* Real InteractiveCards component */}
              {message.role === 'assistant' && cards.length > 0 && (
                <CardErrorBoundary>
                  <InteractiveCards
                    cards={cards}
                    messageId={message.id}
                    isReadOnly={true}
                    handledCardIds={[]}
                    croppedImages={{}}
                    demoExpandedIndex={demoExpandIdx >= 0 ? demoExpandIdx : null}
                  />
                </CardErrorBoundary>
              )}
            </div>
          );
        })}
        
        {/* Completion indicator */}
        {isComplete && usecase?.outcome && (
          <div className="text-center py-6 mt-4 animate-fadeIn">
            <div className="inline-flex items-center gap-2 px-5 py-3 bg-emerald-50 dark:bg-emerald-900/30 rounded-2xl border border-emerald-200 dark:border-emerald-800">
              <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                {usecase.outcome}
              </span>
            </div>
          </div>
        )}
      </div>
      
      {/* Optional footer */}
      {footerContent && (
        <div className="flex-shrink-0">
          {footerContent}
        </div>
      )}

      {/* fadeIn animation */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

/**
 * DemoChatSection - Full demo chat with provider
 * 
 * This is the main component to use. It wraps everything in the provider
 * and renders messages using real app components.
 * 
 * Usage:
 * <DemoChatSection 
 *   usecase={usecaseData} 
 *   autoPlay={true}
 *   className="h-[500px]"
 * />
 */
const DemoChatSection = ({ 
  usecase, 
  autoPlay = true, 
  playbackSpeed = 1,
  onPlaybackComplete,
  className,
  style,
  showHeader = false,
  headerContent,
  footerContent,
  onMessageClick,
  expandedCardId,
}) => {
  if (!usecase) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        No usecase data provided
      </div>
    );
  }

  return (
    <DemoChatProvider 
      usecase={usecase}
      autoPlay={autoPlay}
      playbackSpeed={playbackSpeed}
      onPlaybackComplete={onPlaybackComplete}
    >
      <DemoChatRenderer 
        className={className} 
        style={style}
        showHeader={showHeader}
        headerContent={headerContent}
        footerContent={footerContent}
        onMessageClick={onMessageClick}
        expandedCardId={expandedCardId}
      />
    </DemoChatProvider>
  );
};

/**
 * DemoChatControls - Playback controls
 */
export const DemoChatControls = ({ className }) => {
  const demo = useDemoChat();
  
  if (!demo) return null;
  
  const { 
    isPlaying, 
    isComplete, 
    visibleMessageCount, 
    totalMessages,
    play, 
    pause, 
    reset,
    skipToEnd,
    goToMessage,
  } = demo;

  return (
    <div className={`flex items-center gap-2 ${className || ''}`}>
      <button
        onClick={isPlaying ? pause : play}
        disabled={isComplete}
        className="px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-lg text-sm font-medium disabled:opacity-50"
      >
        {isPlaying ? 'Pause' : 'Play'}
      </button>
      
      <button
        onClick={reset}
        className="px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-lg text-sm font-medium"
      >
        Reset
      </button>
      
      <button
        onClick={skipToEnd}
        disabled={isComplete}
        className="px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-lg text-sm font-medium disabled:opacity-50"
      >
        Skip
      </button>
      
      <div className="flex items-center gap-1.5 ml-2">
        {Array.from({ length: totalMessages }).map((_, i) => (
          <button
            key={i}
            onClick={() => goToMessage(i)}
            className={`w-2 h-2 rounded-full transition-colors ${
              i < visibleMessageCount 
                ? 'bg-emerald-500' 
                : 'bg-gray-300 dark:bg-gray-600'
            }`}
          />
        ))}
      </div>
      
      <span className="text-xs text-gray-500 ml-2">
        {visibleMessageCount}/{totalMessages}
      </span>
    </div>
  );
};

export { DemoChatRenderer };
export default DemoChatSection;
